import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { uniqueId } from '../form/ids';

export interface ConfirmDialogData {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Azione distruttiva: il pulsante di conferma è rosso. */
  readonly danger?: boolean;
}

/**
 * Finestra di conferma su CDK Dialog: fuoco intrappolato, Esc annulla, al termine il fuoco
 * torna dove era. Si apre con `ConfirmDialogService.open()`.
 */
@Component({
  selector: 'bs-confirm-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  host: { class: 'block' },
  template: `
    <div
      class="w-full max-w-md rounded-3xl border border-stone-200 bg-white p-6 shadow-lg dark:border-stone-700 dark:bg-stone-900"
      data-testid="confirm-dialog"
    >
      <h2 [id]="titleId" class="text-xl font-semibold text-ink dark:text-stone-50">
        {{ data.title }}
      </h2>
      <p [id]="messageId" class="mt-2 text-sm text-stone-600 dark:text-stone-300">
        {{ data.message }}
      </p>
      <div class="mt-6 flex flex-wrap justify-end gap-2">
        <bs-button variant="secondary" testId="confirm-dialog-cancel" (click)="ref.close(false)">
          {{ data.cancelLabel ?? 'Annulla' }}
        </bs-button>
        <bs-button
          [variant]="data.danger ? 'danger' : 'primary'"
          testId="confirm-dialog-confirm"
          (click)="ref.close(true)"
        >
          {{ data.confirmLabel ?? 'Conferma' }}
        </bs-button>
      </div>
    </div>
  `,
})
export class ConfirmDialogComponent {
  protected readonly data = inject<ConfirmDialogData & { readonly id?: string }>(DIALOG_DATA);
  protected readonly ref = inject<DialogRef<boolean>>(DialogRef);
  protected readonly titleId = `${this.data.id ?? 'bs-dialog'}-title`;
  protected readonly messageId = `${this.data.id ?? 'bs-dialog'}-message`;
}

@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  private readonly dialog = inject(Dialog);

  /** Apre la conferma; emette `true` solo se si conferma. */
  open(data: ConfirmDialogData): Observable<boolean> {
    const id = uniqueId('bs-dialog');
    const ref = this.dialog.open<
      boolean,
      ConfirmDialogData & { readonly id: string },
      ConfirmDialogComponent
    >(ConfirmDialogComponent, {
      data: { ...data, id },
      ariaLabelledBy: `${id}-title`,
      ariaDescribedBy: `${id}-message`,
      role: 'alertdialog',
      ariaModal: true,
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      hasBackdrop: true,
      backdropClass: ['bg-stone-950/50'],
      panelClass: ['w-full', 'max-w-md', 'px-4'],
    });
    return ref.closed.pipe(map((result) => result === true));
  }
}
